/* eslint-disable @typescript-eslint/restrict-plus-operands */
/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description getAPIFriendlyFilters
 */

import { chomp } from '@zcode/nr/es/shared/utils'
import * as Utils from '@zcode/nr/es/shared/utils/utils'
import type { ApiFriendlyFilters, Filters } from '@zcode/state-type'
import type { Writable } from 'type-fest'
import {
  ALLOWED_SORT_OPTIONS,
  DEFAULT_SORT_OPTION,
  MAX_FILTER_PRICE,
  MAX_NIGHTLY_FILTER_PRICE,
} from '../../constants/filterConstants'
import { logger } from '../../logger'
import { isDateValid } from './dateHelpers'
import { getShouldShowNightlyPrice } from './getShouldShowNightlyPrice'

interface FilterTypes extends Partial<Filters> {
  buildingIds?: number[]
  listingIds?: number[]
}

export const getAPIFriendlyFilters = ({
  filters,
}: {
  filters: FilterTypes
}) => {
  const { startDate: filterStartDate, endDate: filterEndDate, longTerm: filterLongTerm } = filters
  const isShortTerm = !filterLongTerm
  const shouldShowNightlyPrice = getShouldShowNightlyPrice({
    filterEndDate,
    filterStartDate,
    longTerm: filterLongTerm,
  })
  const excludeMaxPrice = filters.maxPrice === MAX_FILTER_PRICE && !filters.customMaxPrice
  const excludeMaxNightlyPrice = filters.maxNightlyPrice === MAX_NIGHTLY_FILTER_PRICE

  // sort should be null if selected sort is DEFAULT_SORT_OPTION, otherwise sort should be an array
  const sort =
    filters.sort?.length && ALLOWED_SORT_OPTIONS.has(filters.sort[0])
      ? filters.sort[0] === DEFAULT_SORT_OPTION
        ? null
        : [filters.sort[0]]
      : undefined

  const maxNightlyPrice =
    excludeMaxNightlyPrice || filters.maxNightlyPrice == null ? null : filters.maxNightlyPrice * 30
  const maxMonthlyPrice = excludeMaxPrice ? null : filters.maxPrice
  const isNightly = isShortTerm && shouldShowNightlyPrice

  const { bedrooms, maxNightlyPrice: _, minNightlyPrice, propertyCategories, ...rest } = filters
  // properties that need to be cleaned up before
  const query: Writable<ApiFriendlyFilters> = Object.assign({}, rest, {
    endDate: isDateValid(filters.endDate) ? filters.endDate : null,
    startDate: isDateValid(filters.startDate) ? filters.startDate : null,
    maxLat: filters.maxLat ? chomp(filters.maxLat, 5) : null,
    minLat: filters.minLat ? chomp(filters.minLat, 5) : null,
    maxLng: filters.maxLng ? chomp(filters.maxLng, 5) : null,
    minLng: filters.minLng ? chomp(filters.minLng, 5) : null,
    maxPrice: isNightly ? maxNightlyPrice : maxMonthlyPrice,
    minPrice: isNightly
      ? minNightlyPrice == null
        ? null
        : minNightlyPrice * 30
      : filters.minPrice,
    sort,
    sources: null,
  })

  if (isShortTerm) {
    if (filters.shortTerm) {
      query.shortTerm = true
      query.longTerm = false

      if (filters.guests?.length) {
        query.guests = filters.guests
      }
    }
  } else {
    delete query.guests
  }

  // setting different limit for favorites since they load all at once
  if (filters.byIds) {
    query.limit = (filters.listingIds ?? []).length + (filters.buildingIds ?? []).length
  }
  if (query.sort === undefined) {
    delete query.sort
  }
  if (query.hood === undefined) {
    delete query.hood
  }
  if (typeof query.keywords !== 'string') {
    delete query.keywords
  }
  if (Object.prototype.hasOwnProperty.call(query, 'hasImages') && !query.hasImages) {
    delete query.hasImages
  }
  if (query.name) {
    delete query.name
  }
  if (query.searchId) {
    delete query.searchId
  }
  if (query.hideNoVacancy) {
    delete query.hideNoVacancy
  }

  if (query.rating?.min === null && query.rating?.max === null) {
    delete query.rating
  }

  if (query.externalRating?.min === null && query.externalRating?.max === null) {
    delete query.externalRating
  }

  // Bedrooms
  query.bedrooms = Utils.boolArrayToIntArray(bedrooms)
  // since our filter logic is non-selected equals to all-selected -> passing null for none and all cases
  // TODO get "6" from the default filter bedroom
  if (!query.bedrooms?.length || query.bedrooms.length === 6) {
    query.bedrooms = null
  }
  if (query.bedrooms?.includes(5)) {
    const min = Math.min(...query.bedrooms)

    query.bedrooms = [min, 20]
  }
  // Property Category
  const pCatSize = propertyCategories ? Object.keys(propertyCategories).length : undefined
  query.propertyCategories = Utils.omapFiltered(propertyCategories, (v, k) => {
    return v ? Utils.toSnakeCase(k, false) : undefined
  })
  // all-selected and non-selected
  if (!query.propertyCategories.length || query.propertyCategories.length === pCatSize) {
    query.propertyCategories = null
  }

  // Short term and long term - nullify if they're false
  const { longTerm, shortTerm } = query

  if (!shortTerm && !longTerm) {
    query.shortTerm = false
    query.longTerm = false
  } else if (shortTerm && !longTerm) {
    query.longTerm = false
  } else if (!shortTerm && longTerm) {
    query.shortTerm = false
  }

  // allow external like we do in djz
  query.external = true
  // drop url if this query has box
  const { box, minLat, maxLat, minLng, maxLng } = query
  if ((maxLat && minLat && maxLng && minLng) || box) {
    // safe-guard against maxLat > minLat
    if (maxLat && minLat && maxLat < minLat) {
      query.minLat = maxLat
      query.maxLat = minLat
    }
    // safe-guard against maxLng < minLng
    if (maxLng && minLng && maxLng < minLng) {
      query.minLng = maxLng
      query.maxLng = minLng
    }

    if (maxLat === minLat || maxLng === minLng) {
      logger.info('invalid lat/lng in getAPIFriendlyFilters', query)
      if (query.url) {
        delete query.minLat
        delete query.minLng
        delete query.maxLat
        delete query.maxLng
      } else {
        const epsilon = 0.05
        if (query.maxLat && query.minLat && maxLat === minLat) {
          query.minLat -= epsilon
          query.maxLat += epsilon
        }
        if (query.maxLng && query.minLng && maxLng === minLng) {
          query.minLng -= epsilon
          query.maxLng += epsilon
        }
        delete query.url
      }
    } else {
      delete query.url
    }
  }

  // mmFrom is used for querying listables for display in multi-message modal
  if (filters.mmFrom) {
    query.mmFrom = filters.mmFrom
  }

  const filteredQuery: Partial<typeof query> = Object.fromEntries(
    Utils.omapFiltered(query, (...[v, k]) => {
      const isArray = Array.isArray(v)
      if ((isArray && v.length) || (!isArray && v !== null)) {
        return [k, v] as const
      }
    })
  )
  // P param for savedSeach
  if (query.P !== undefined) {
    filteredQuery.P = query.P
  }
  // encodedFilters for Select chrome extension, when `encodedFilters` is set it will take a
  // priority over the existing P,O and V tokens
  if (query.encodedFilters) {
    delete filteredQuery.P
    delete filteredQuery.O
    filteredQuery.V = query.encodedFilters
  }

  // qs param is for creating multi-queries and is an encoded array of filters
  if (query.qs) {
    filteredQuery.qs = query.qs
  }

  // clear out customMaxPrice (not a real value)
  if (filteredQuery.customMaxPrice !== undefined) {
    delete filteredQuery.customMaxPrice
  }

  return filteredQuery
}
